import { ApolloLink } from '@apollo/client';
import {
  getHasUserSelectedCelsiusUnit,
  getHasUserSelectedKgPerHectoliterUnit,
  getHasUserSelectedKilogramUnit,
  getHasUserSelectedMeterUnit,
  UnitsConverter,
} from '../../util';

// Convert metric distance inputs (meters, cm) back to imperial (feet, inches) before
// sending to the server. The server always stores distances in imperial units.
const parseLocaleDistanceConversions = (variables: Record<string, unknown>) => {
  const fields: Record<string, unknown> = {};
  const variablesKeys = Object.keys(variables);
  if (variablesKeys.includes('height_ft') && variables['height_ft'] !== null) {
    fields['height_ft'] = UnitsConverter.convertMetersToFeet(variables.height_ft as number);
  }

  if (variablesKeys.includes('diameter_ft') && variables['diameter_ft'] !== null) {
    fields['diameter_ft'] = UnitsConverter.convertMetersToFeet(variables.diameter_ft as number);
  }

  if (variablesKeys.includes('diameter') && variables['diameter'] !== null) {
    fields['diameter'] = UnitsConverter.convertCentimetersToInches(variables.diameter as number);
  }

  if (variablesKeys.includes('hub_offset_ft') && variables['hub_offset_ft'] !== null) {
    fields['hub_offset_ft'] = UnitsConverter.convertMetersToFeet(variables.hub_offset_ft as number);
  }

  if (
    variablesKeys.includes('hubs') &&
    variables['hubs'] !== null &&
    variables['hubs'] &&
    Array.isArray(variables.hubs) &&
    variables.hubs.length > 0
  ) {
    fields['hubs'] = (variables.hubs as Record<string, unknown>[]).map((hub) => {
      const hubKeys = Object.keys(hub);
      return {
        ...hub,
        ...(hubKeys.includes('hub_offset_ft') && {
          hub_offset_ft: UnitsConverter.convertMetersToFeet(hub.hub_offset_ft as number),
        }),
      };
    });
  }

  return fields;
};

// Convert Celsius temperature inputs back to Fahrenheit for the server
const parseLocaleTemperatureConversions = (variables: Record<string, unknown>) => {
  const fields: Record<string, unknown> = {};
  const variablesKeys = Object.keys(variables);

  if (variablesKeys.includes('current_grain_temp') && variables['current_grain_temp'] !== null) {
    fields['current_grain_temp'] = UnitsConverter.convertCelsiusToFahrenheit(
      variables.current_grain_temp as number
    );
  }

  return fields;
};

// Convert kilogram mass inputs back to pounds for the server
const parseLocaleKilogramMassConversions = (variables: Record<string, unknown>) => {
  const fields: Record<string, unknown> = {};
  const variablesKeys = Object.keys(variables);

  if (variablesKeys.includes('weight_in_lbs') && variables['weight_in_lbs'] !== null) {
    fields['weight_in_lbs'] = UnitsConverter.convertKilogramToPound(
      variables.weight_in_lbs as number
    );
  }
  return fields;
};

// Convert kg/hL test weight inputs back to lbs/bu for the server
const parseLocaleKgPerHectoliterUnitConversions = (variables: Record<string, unknown>) => {
  const fields: Record<string, unknown> = {};
  const variablesKeys = Object.keys(variables);

  if (variablesKeys.includes('test_weight_in_lbs') && variables['test_weight_in_lbs'] !== null) {
    fields['test_weight_in_lbs'] = UnitsConverter.convertKilogramPerHectoliterToPoundPerBu(
      variables.test_weight_in_lbs as number
    );
  }
  return fields;
};

// Orchestrator: check each unit preference flag and apply the corresponding
// reverse conversion so mutation variables are always sent in imperial units
const parseUnitsConversions = (variables: Record<string, unknown> | undefined) => {
  let fields: Record<string, unknown> = {};
  if (!variables) return fields;

  const hasUserSelectedMeterUnit = getHasUserSelectedMeterUnit();
  const hasUserSelectedCelsiusUnit = getHasUserSelectedCelsiusUnit();
  const hasUserSelectedKilogramUnit = getHasUserSelectedKilogramUnit();
  const hasUserSelectedKgPerHectoliterUnit = getHasUserSelectedKgPerHectoliterUnit();

  if (hasUserSelectedMeterUnit) {
    fields = { ...fields, ...parseLocaleDistanceConversions(variables) };
  }

  if (hasUserSelectedCelsiusUnit) {
    fields = { ...fields, ...parseLocaleTemperatureConversions(variables) };
  }

  if (hasUserSelectedKilogramUnit) {
    fields = { ...fields, ...parseLocaleKilogramMassConversions(variables) };
  }

  if (hasUserSelectedKgPerHectoliterUnit) {
    fields = { ...fields, ...parseLocaleKgPerHectoliterUnitConversions(variables) };
  }

  return fields;
};

// Apollo Link that intercepts outgoing operations and converts metric mutation
// variables back to imperial before they reach the server. This is the request-side
// counterpart to the Transformer_Link (which converts responses from imperial to metric).
// Apollo Client 4.x deprecated the named `Operation` export — use the namespace accessor.
// `ApolloLink.ForwardFunction` is the only way to reference the forward function type.
class VariablesLink extends ApolloLink {
  request(operation: ApolloLink.Operation, forward: ApolloLink.ForwardFunction) {
    operation.variables = {
      ...operation.variables,
      ...parseUnitsConversions(operation.variables),
    };
    return forward(operation);
  }
}

export { VariablesLink };
